'use client';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowRightLeft, CreditCard, Loader2, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '@/lib/api/trpc';

const PROVIDER_LABELS: Record<string, string> = {
  stripe: 'Stripe',
  checkout: 'Checkout.com',
  adyen: 'Adyen',
};

type ProfileSummary = {
  status: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
} | null;

function describeProfile(profile: ProfileSummary, none: string): string {
  if (!profile || !profile.last4) return none;
  const brand = profile.brand ? profile.brand.toUpperCase() : 'CARD';
  const expiry =
    profile.expMonth && profile.expYear
      ? ` · ${String(profile.expMonth).padStart(2, '0')}/${profile.expYear}`
      : '';
  return `${brand} •••• ${profile.last4}${expiry}`;
}

/**
 * Which PSP bills this account's CRM fees, and the move to another one.
 * A move only becomes effective once the merchant has a verified card on
 * the target provider (docs/MERCHANT_BILLING_PROVIDERS.md).
 */
export function BillingProviderCard() {
  const { t } = useTranslation('common');
  const utils = trpc.useUtils();
  const assignment = trpc.billing.getProviderAssignment.useQuery({});

  const invalidate = () => {
    utils.billing.getProviderAssignment.invalidate();
    utils.billing.getPaymentMethodStatus.invalidate();
  };
  const requestMigration = trpc.billing.requestProviderMigration.useMutation({
    onSuccess: (result) => {
      toast.success(
        result.switched
          ? t('accountManager.billingProvider.toasts.switched')
          : t('accountManager.billingProvider.toasts.requested'),
      );
      invalidate();
    },
    onError: (error) => toast.error(error.message),
  });
  const cancelMigration = trpc.billing.cancelProviderMigration.useMutation({
    onSuccess: () => {
      toast.success(t('accountManager.billingProvider.toasts.cancelled'));
      invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const data = assignment.data;
  const target = data?.provider === 'stripe' ? 'checkout' : 'stripe';
  const isBusy = requestMigration.isPending || cancelMigration.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="h-4 w-4" />
          {t('accountManager.billingProvider.title')}
        </CardTitle>
        <CardDescription>{t('accountManager.billingProvider.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {assignment.isLoading || !data ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="green">{PROVIDER_LABELS[data.provider] ?? data.provider}</Badge>
              <span className="text-muted-foreground text-sm">
                {describeProfile(data.currentProfile, t('accountManager.billingProvider.noCard'))}
              </span>
            </div>

            {data.pendingProvider && (
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm">
                <ArrowRightLeft className="h-4 w-4 text-yellow-700" />
                <span>
                  {t('accountManager.billingProvider.pending', {
                    provider: PROVIDER_LABELS[data.pendingProvider] ?? data.pendingProvider,
                  })}
                </span>
                <span className="text-muted-foreground">
                  {describeProfile(data.pendingProfile, t('accountManager.billingProvider.noCard'))}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => cancelMigration.mutate({})}
                >
                  {cancelMigration.isPending ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  ) : (
                    <XCircle className="mr-2 h-3 w-3" />
                  )}
                  {t('accountManager.billingProvider.cancel')}
                </Button>
              </div>
            )}

            {!data.pendingProvider && (target === 'stripe' || data.vaultBillingEnabled) && (
              <Button
                size="sm"
                variant="outline"
                disabled={isBusy}
                onClick={() => requestMigration.mutate({ provider: target })}
              >
                {requestMigration.isPending ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : (
                  <ArrowRightLeft className="mr-2 h-3 w-3" />
                )}
                {t('accountManager.billingProvider.migrate', { provider: PROVIDER_LABELS[target] })}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
